import {ArrayUtils} from '@colonist/utils'
import {BaseCardData} from '@colonist/game-card-types'
import {CardEnum, CardTypeEnum} from '@colonist/game-states'
import {getResourceCardData} from './ResourceCardData.ts'
import {getAnyResourceCardData} from './AnyResourceCardData.ts'
import {getDevelopmentCardData} from './DevelopmentCardData.ts'
import {getCommodityCardData} from './CommodityCardData.ts'
import {getProgressCardDataScience} from './ProgressCardDataScience.ts'
import {getProgressCardDataTrade} from './ProgressCardDataTrade.ts'
import {getProgressCardDataPolitics} from './ProgressCardDataPolitics.ts'
import {getMiscellaneousCardData} from './MiscellaneousCardData.ts'

export class CardDataController {
    private static readonly cardDataList: readonly BaseCardData[] = [
        ...getResourceCardData(),
        ...getCommodityCardData(),
        ...getAnyResourceCardData(),
        ...getDevelopmentCardData(),
        ...getProgressCardDataScience(),
        ...getProgressCardDataTrade(),
        ...getProgressCardDataPolitics(),
        ...getMiscellaneousCardData(),
    ]

    private static readonly cardMap = new Map<CardEnum, BaseCardData>(this.cardDataList.map((card) => [card.cardEnum, card]))
    private static readonly cardToCardBackMap = new Map<CardEnum, CardEnum | undefined>(this.cardDataList.map((card) => [
        card.cardEnum,
        this.cardDataList
            .filter((possibleBackCard) => this.cardMap.get(card.cardEnum)?.cardTypeEnum == possibleBackCard.cardTypeEnum)
            .find((possibleBackCard) => possibleBackCard.isCardBack)
            ?.cardEnum,
    ]))

    static getCardDataForCard(cardEnum: CardEnum): BaseCardData | undefined {
        if(typeof cardEnum == 'string') cardEnum = parseInt(cardEnum, 10)
        return this.cardMap.get(cardEnum)
    }

    static getBaseCardDataForType(cardTypes: CardTypeEnum[]): BaseCardData[] {
        const cardEnums = this.getCardEnumsOfTypes(cardTypes)
        const cardDataForType: BaseCardData[] = []
        for(const cardEnum of cardEnums) {
            const cardData = this.getCardDataForCard(cardEnum)
            if(cardData == undefined) continue
            cardDataForType.push(cardData)
        }
        return cardDataForType
    }

    static getCardTypeFromCardEnum(cardEnum: CardEnum): CardTypeEnum | undefined {
        return this.getCardDataForCard(cardEnum)?.cardTypeEnum
    }

    static getCardBackOfCard(cardEnum: CardEnum): CardEnum | undefined {
        if(typeof cardEnum == 'string') cardEnum = parseInt(cardEnum, 10)
        return this.cardToCardBackMap.get(cardEnum)
    }

    static getCardEnumsOfTypes(cardTypes: CardTypeEnum[]): CardEnum[] {
        const cardEnums: CardEnum[] = []
        for(const card of this.cardDataList) {
            if(card.isCardBack) continue
            if(!ArrayUtils.elementInArray(cardTypes, card.cardTypeEnum)) continue
            cardEnums.push(card.cardEnum)
        }
        return cardEnums
    }

    static areCardsValidInputForType(cardsToCheck: CardEnum[], validCardTypes: CardTypeEnum[]): boolean {
        const uniqueCardsToCheck = ArrayUtils.removeDuplicatesFromArray(cardsToCheck)
        for(const cardEnum of uniqueCardsToCheck) {
            const cardData = this.getCardDataForCard(cardEnum)
            if(cardData == undefined) continue
            if(cardData.isCardBack) return false
            if(!ArrayUtils.elementInArray(validCardTypes, cardData.cardTypeEnum)) return false
        }
        return true
    }

    static isResourceCard(cardEnum: CardEnum): boolean {
        const cardData = this.getCardDataForCard(cardEnum)
        if(cardData == undefined) return false

        if(cardData.cardTypeEnum == CardTypeEnum.Resource) return true
        if(cardData.cardTypeEnum == CardTypeEnum.Commodity) return true

        return false
    }

    static getCardBacksForCards(cardEnums: CardEnum[]): CardEnum[] {
        return cardEnums
            .map((cardEnum) => this.getCardBackOfCard(cardEnum))
            .filter((cardEnum) => cardEnum != undefined)
    }
}
